import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { fetchContent, fetchBranding, fetchMenu, getImageUrl } from '../api/api';
import { useCart } from '../context/CartContext';
import { useUser } from '../context/UserContext';
import { useCompare } from '../context/CompareContext';
import { useCurrency } from '../context/CurrencyContext';
import './Header.css';

function ToplineRotator({ items }) {
  const [idx, setIdx] = useState(0);
  useEffect(() => {
    if (!items || items.length <= 1) return;
    const timer = setInterval(() => setIdx(i => (i + 1) % items.length), 3500);
    return () => clearInterval(timer);
  }, [items]);
  if (!items || items.length === 0) return null;
  return (
    <div className="topline-rotator">
      <div className="custom-text-item" dangerouslySetInnerHTML={{ __html: items[idx] }} />
    </div>
  );
}

const NavLink = ({ path, children, onClick }) =>
  path.startsWith('http') ? (
    <a href={path} target="_blank" rel="noopener noreferrer" onClick={onClick}>{children}</a>
  ) : (
    <Link to={path} onClick={onClick}>{children}</Link>
  );

function CartDrawer({ open, onClose }) {
  const { cart, removeFromCart, updateQty, subtotal } = useCart();
  const currency = useCurrency();
  const navigate = useNavigate();

  const imgSrc = (item) => {
    const url = item.images?.[0] || item.image || '';
    if (!url) return '/images/scooter-1.jpg';
    return getImageUrl(url);
  };

  return (
    <>
      <div className={`cart-drawer-backdrop ${open ? 'open' : ''}`} onClick={onClose} />
      <div className={`cart-drawer ${open ? 'open' : ''}`}>
        <div className="cart-drawer-header">
          <p className="cart-drawer-title">Your Cart</p>
          <button className="cart-drawer-close" onClick={onClose}>&#10005;</button>
        </div>
        {cart.length === 0 ? (
          <div className="cart-drawer-empty">
            <span></span>
            <p>Your cart is empty</p>
            <button className="btn" onClick={onClose}>Continue Shopping</button>
          </div>
        ) : (
          <>
            <div className="cart-drawer-items">
              {cart.map(item => (
                <div key={item._id} className="cart-drawer-item">
                  <img src={imgSrc(item)} alt={item.name} onError={e => { e.target.src = '/images/scooter-1.jpg'; }} />
                  <div className="cdi-info">
                    <span className="cdi-name">{item.name}</span>
                    <span className="cdi-price">{currency}{item.price.toFixed(2)}</span>
                    <div className="cdi-qty">
                      <button onClick={() => updateQty(item._id, item.qty - 1)}>&#8722;</button>
                      <span>{item.qty}</span>
                      <button onClick={() => updateQty(item._id, item.qty + 1)}>+</button>
                    </div>
                  </div>
                  <div className="cdi-right">
                    <button className="cdi-remove" onClick={() => removeFromCart(item._id + (item.selectedColor?.name || ''))}>&#10005;</button>
                    <span className="cdi-subtotal">{currency}{(item.price * item.qty).toFixed(2)}</span>
                  </div>
                </div>
              ))}
            </div>
            <div className="cart-drawer-footer">
              <div className="cart-drawer-subtotal">
                <span>Subtotal</span>
                <strong>{currency}{subtotal.toFixed(2)}</strong>
              </div>
              <button className="btn cart-drawer-checkout" onClick={() => { onClose(); navigate('/cart'); }}>
                View Cart &amp; Checkout
              </button>
            </div>
          </>
        )}
      </div>
    </>
  );
}

function Header() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [openItems, setOpenItems] = useState({});
  const [openGroups, setOpenGroups] = useState({});
  const [menu, setMenu] = useState([]);
  const [topline, setTopline] = useState([]);
  const [logo, setLogo] = useState('');
  const [siteName, setSiteName] = useState('Green Wheels Scooters');
  const [headerIcons, setHeaderIcons] = useState({ showCompare: true, showUser: true, showCart: true });
  const { itemCount, cartDrawerOpen, openCartDrawer, closeCartDrawer } = useCart();
  const { user, logout } = useUser();
  const { compareList } = useCompare();
  const navigate = useNavigate();
  const location = useLocation();
  const isAdmin = location.pathname.startsWith('/admin');

  useEffect(() => {
    fetchContent('header').then(data => {
      if (!data) return;
      setTopline(data.topline || []);
      setHeaderIcons({
        showCompare: data.showCompare !== false,
        showUser:    data.showUser    !== false,
        showCart:    data.showCart    !== false,
      });
    }).catch(() => {});
    fetchBranding().then(b => {
      if (b.siteName) setSiteName(b.siteName);
      if (b.logo) setLogo(getImageUrl(b.logo, 220));
    }).catch(() => {});
    fetchMenu().then(items => { if (items.length > 0) setMenu(items); }).catch(() => {});
  }, []);

  const closeAll = () => { setMobileOpen(false); setOpenItems({}); setOpenGroups({}); };
  const toggleItem = (e, idx) => { e.preventDefault(); e.stopPropagation(); setOpenItems(prev => ({ ...prev, [idx]: !prev[idx] })); };
  const toggleGroup = (e, key) => { e.preventDefault(); e.stopPropagation(); setOpenGroups(prev => ({ ...prev, [key]: !prev[key] })); };

  return (
    <>
      <header className="header">
        <div className="hdr-topline">
          <div className="container">
            <div className="hdr-topline-center">
              <ToplineRotator items={topline} />
            </div>
          </div>
        </div>

        <div className="hdr-content">
          <div className="container">
            <div className="header-content">

              {/* 1. Hamburger */}
              <button
                className="mobile-toggle"
                onClick={() => { setMobileOpen(o => !o); setOpenItems({}); setOpenGroups({}); }}
                aria-label="Toggle menu"
              >
                {mobileOpen
                  ? <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
                  : <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><line x1="3" y1="6" x2="21" y2="6"/><line x1="3" y1="12" x2="21" y2="12"/><line x1="3" y1="18" x2="21" y2="18"/></svg>
                }
              </button>

              {/* 2. Logo */}
              <Link to="/" className="logo" onClick={closeAll}>
                {logo ? <img src={logo} alt={siteName} width="220" height="80" onError={e => { e.target.style.display = 'none'; e.target.nextSibling && (e.target.nextSibling.style.display = 'block'); }} /> : null}
                <span className="logo-text" style={{ display: logo ? 'none' : 'block' }}>{siteName}</span>
              </Link>

              {/* 3. Nav + Actions grouped together on the right */}
              <div className="header-right">
              <nav className="nav nav-desktop">
                {menu.map((item, idx) => {
                  const hasSub = item.submenu?.length > 0;
                  const isOpen = !!openItems[idx];
                  return (
                    <div key={idx} className={`nav-item${hasSub ? ' has-submenu' : ''}`}>
                      <div className="nav-item-row">
                        <NavLink path={item.path} onClick={closeAll}>{item.name}</NavLink>
                        {hasSub && (
                          <button className="mob-toggle-btn" onClick={(e) => toggleItem(e, idx)} aria-label="expand">
                            {isOpen ? '▲' : '▼'}
                          </button>
                        )}
                      </div>
                      {hasSub && (
                        <div className="submenu">
                          {item.submenu.map((sub, i) =>
                            sub.children?.length > 0 ? (
                              <div key={i} className="submenu-group">
                                <div className="submenu-group-label" onClick={(e) => toggleGroup(e, `${idx}-${i}`)}>
                                  <span>{sub.name}</span>
                                </div>
                                <div className="submenu-children">
                                  {sub.children.map((child, ci) => (
                                    <NavLink key={ci} path={child.path} onClick={closeAll}>{child.name}</NavLink>
                                  ))}
                                </div>
                              </div>
                            ) : (
                              <NavLink key={i} path={sub.path} onClick={closeAll}>{sub.name}</NavLink>
                            )
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </nav>

              {/* 4. Actions */}
              <div className="header-actions">
                {!isAdmin && headerIcons.showCompare && (
                  <Link to="/compare" className="compare-icon-btn" aria-label="Compare" title="Compare">
                    <span className="compare-icon-wrap">
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M8 3H5a2 2 0 00-2 2v14a2 2 0 002 2h3"/>
                        <path d="M16 3h3a2 2 0 012 2v14a2 2 0 01-2 2h-3"/>
                        <line x1="12" y1="3" x2="12" y2="21"/>
                      </svg>
                      {compareList?.length > 0 && <span className="compare-icon-dot" />}
                    </span>
                    <span className="compare-icon-label">Compare</span>
                  </Link>
                )}
                {headerIcons.showUser && (
                  <div className="user-icon-wrap">
                    <button
                      className="user-icon-btn"
                      aria-label={user ? 'Account menu' : 'Sign in'}
                      onClick={() => user ? setUserMenuOpen(o => !o) : navigate('/login')}
                      title={user ? user.name : 'Sign In'}
                    >
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2"/>
                        <circle cx="12" cy="7" r="4"/>
                      </svg>
                      {user && <span className="user-icon-dot" />}
                    </button>
                    {user && userMenuOpen && (
                      <>
                        <div className="user-menu-backdrop" onClick={() => setUserMenuOpen(false)} />
                        <div className="user-menu">
                          <div className="user-menu-name">&#128100; {user.name}</div>
                          <Link to="/orders" className="user-menu-item" onClick={() => setUserMenuOpen(false)}> My Orders</Link>
                          <button className="user-menu-item user-menu-signout" onClick={() => { logout(); setUserMenuOpen(false); navigate('/'); }}>&#128682; Sign Out</button>
                        </div>
                      </>
                    )}
                  </div>
                )}
                {headerIcons.showCart && (
                  <button className="cart-icon-btn" onClick={() => openCartDrawer()} aria-label="Open cart">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M6 2L3 6v14a2 2 0 002 2h14a2 2 0 002-2V6l-3-4z"/>
                      <line x1="3" y1="6" x2="21" y2="6"/>
                      <path d="M16 10a4 4 0 01-8 0"/>
                    </svg>
                    {itemCount > 0 && <span className="cart-icon-badge">{itemCount > 99 ? '99+' : itemCount}</span>}
                  </button>
                )}
              </div>
              </div>

            </div>

            {/* Mobile nav — outside flex row, rendered below header-content */}
            <nav className={`nav nav-mobile${mobileOpen ? ' nav-open' : ''}`}>
              {menu.map((item, idx) => {
                const hasSub = item.submenu?.length > 0;
                const isOpen = !!openItems[idx];
                return (
                  <div key={idx} className={`nav-item${hasSub ? ' has-submenu' : ''}${isOpen ? ' mob-open' : ''}`}>
                    <div className="nav-item-row">
                      <NavLink path={item.path} onClick={closeAll}>{item.name}</NavLink>
                      {hasSub && (
                        <button className="mob-toggle-btn" onClick={(e) => toggleItem(e, idx)} aria-label="expand">
                          {isOpen ? '▲' : '▼'}
                        </button>
                      )}
                    </div>
                    {hasSub && (
                      <div className={`submenu${isOpen ? ' mob-submenu-open' : ''}`}>
                        {item.submenu.map((sub, i) =>
                          sub.children?.length > 0 ? (
                            <div key={i} className={`submenu-group${openGroups[`${idx}-${i}`] ? ' mob-group-open' : ''}`}>
                              <div className="submenu-group-label" onClick={(e) => toggleGroup(e, `${idx}-${i}`)}>
                                <span>{sub.name}</span>
                                <span className="mob-group-arrow">{openGroups[`${idx}-${i}`] ? '▲' : '▼'}</span>
                              </div>
                              <div className="submenu-children">
                                {sub.children.map((child, ci) => (
                                  <NavLink key={ci} path={child.path} onClick={closeAll}>{child.name}</NavLink>
                                ))}
                              </div>
                            </div>
                          ) : (
                            <NavLink key={i} path={sub.path} onClick={closeAll}>{sub.name}</NavLink>
                          )
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </nav>

          </div>
        </div>

        {/* Backdrop outside flex row */}
        {mobileOpen && <div className="mob-nav-backdrop" onClick={closeAll} />}
      </header>

      <CartDrawer open={cartDrawerOpen} onClose={() => closeCartDrawer()} />
    </>
  );
}

export default Header;
